import { createTheme } from "@mui/material"
import { colors, colorsBeeline } from "./colors"

declare module "@mui/material/styles" {
  interface TypographyVariants {
    h4Bold: React.CSSProperties
    h5Bold: React.CSSProperties
    h7: React.CSSProperties
    h7Pale: React.CSSProperties
  }

  interface TypographyVariantsOptions {
    h4Bold?: React.CSSProperties
    h5Bold?: React.CSSProperties
    h7?: React.CSSProperties
    h7Pale?: React.CSSProperties
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    h4Bold: true
    h5Bold: true
    h7: true
    h7Pale: true
  }
}

export const theme = createTheme({
  palette: {
    primary: {
      main: colors.yellow,
    },
    secondary: {
      main: colors.lightGrey,
    },
  },
  typography: {
    h1: {
      fontSize: 54,
      fontWeight: 400,
      lineHeight: "72px",
      color: colors.lightBlack,
    },
    h2: {
      fontSize: 42,
      lineHeight: "54px",
      color: colors.lightBlack,
    },
    h3: {
      fontSize: 22,
      fontWeight: 500,
      lineHeight: "28px",
      color: colors.lightBlack,
    },
    h4: {
      fontSize: 18,
      lineHeight: "24px",
      color: colors.lightBlack,
    },
    h4Bold: {
      fontSize: 18,
      fontWeight: 500,
      lineHeight: "24px",
      color: colors.lightBlack,
    },
    h5: {
      fontSize: 16,
      lineHeight: "20px",
      color: colors.darkGrey,
    },
    h5Bold: {
      fontSize: 16,
      fontWeight: 500,
      lineHeight: "20px",
      color: colors.darkGrey,
    },
    h6: {
      fontSize: 14,
      lineHeight: "18px",
      color: colors.darkGrey,
    },
    h7: {
      fontSize: 14,
      lineHeight: "18px",
      color: colors.lightBlack,
    },
    h7Pale: {
      fontSize: 14,
      lineHeight: "18px",
      color: colors.darkGrey,
    },
    subtitle2: {
      fontSize: 12,
      color: colors.darkGrey,
      textAlign: "center",
      marginTop: "auto",
    },
  },
  components: {
    MuiButton: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: ({ ownerState }) => ({
          boxShadow: "none",
          textTransform: "none",
          borderRadius: "10px",

          "&:hover": {
            boxShadow: "none",
          },

          "&.Mui-disabled": {
            opacity: 0.5,
            backgroundColor: colors.yellow,
          },

          "& .MuiFormHelperText-root": {
            margin: "4px 0 0 1px",
          },

          ...(ownerState.variant === "contained" &&
            ownerState.color === "primary" && {
              color: colors.black,
            }),

          ...(ownerState.variant === "contained" &&
            ownerState.color === "info" && {
              background: colors.darkPurple,
            }),

          ...(ownerState.variant === "contained" &&
            ownerState.color === "secondary" && {
              color: colors.black,
            }),

          ...(ownerState.size === "medium" && {
            height: 54,
          }),

          ...(ownerState.variant === "text" &&
            ownerState.color === "primary" &&
            ownerState.size === "large" && {
              color: colors.blue,
              background: "transparent",
              fontSize: 22,
              fontWeight: "bold",
              lineHeight: 1,
              padding: 0,
            }),

          ...(ownerState.variant === "text" &&
            ownerState.color === "primary" &&
            ownerState.size === "medium" && {
              color: colors.blue,
              background: "transparent",
              fontSize: 16,
              fontWeight: 400,
              lineHeight: 1,
              padding: 0,
            }),
        }),
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          padding: 2,
        },
        switchBase: {
          margin: 2,
          padding: 2,
          transform: "translateX(0px)",
          "&.Mui-checked": {
            transform: "translateX(20px)",
          },
        },
        thumb: {
          width: 30,
          height: 30,
          color: colors.white,
        },
        track: {
          height: 34,
          width: 56,
          borderRadius: 34 / 2,
          opacity: "1 !important",
          background: colors.lightGrey,
          "&.Mui-checked": {
            background: colors.yellow,
            opacity: 1,
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: "filled",
      },
      styleOverrides: {
        root: ({ ownerState }) => ({
          "& .MuiFormHelperText-root": {
            margin: "4px 0 0 1px",
          },
          "& .MuiInputAdornment-root.MuiInputAdornment-positionStart": {
            marginTop: "0px !important",
          },
          ...(ownerState.variant === "filled" &&
            ownerState.color === "primary" && {
              background: "transparent",
              border: "1px red",
              "& .MuiFilledInput-underline": {
                "&::before": {
                  borderBottom: "none !important",
                },
                "&::after": {
                  borderBottom: "none !important",
                },
              },
              "& .MuiInputBase-root": {
                background: colors.lightGrey,
                borderRadius: 10,
                border: "2px solid transparent",
                transition: "border-color 0.2s",
                "&:hover": {
                  borderColor: colors.blue,
                },
                "&:focus-within": {
                  borderColor: colors.blue,
                },
              },
              "& input": {
                padding: "14px 18px",
                fontSize: 16,
                letterSpacing: 0,
              },
              "& .MuiInputBase-root.Mui-error": {
                borderColor: colors.red,
              },
            }),
          ...(ownerState.variant === "outlined" &&
            ownerState.color === "primary" && {
              background: "transparent",
              "& .MuiInputBase-root": {
                background: "transparent",
                borderRadius: 10,
              },
              "& input": {
                padding: "14px 18px",
                fontSize: 16,
              },
            }),
        }),
      },
    },
    MuiInput: {
      styleOverrides: {
        underline: {
          "&:before": {
            borderBottomWidth: "2px",
            borderColor: colors.paleGray,
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: 14,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          "&.Mui-focused": {
            color: "inherit",
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.variant === "filled" && {
            backgroundColor: colors.lightGrey,
          }),
        }),
        label: ({ ownerState }) => ({
          ...(ownerState.variant === "filled" && {
            color: `${colors.lightBlack}99`,
            fontSize: 16,
            fontWeight: 500,
            padding: "0 14px",
          }),
        }),
      },
    },
  },
})

export const themeBeeline = createTheme({
  ...theme,
  palette: {
    primary: {
      main: colorsBeeline.black,
    },
    secondary: {
      main: colorsBeeline.lightBlack,
    },
  },

  typography: {
    fontFamily: "Beeline Sans, sans-serif",
    h1: {
      fontSize: 16,
      fontWeight: 500,
      lineHeight: "20px",
      color: colorsBeeline.lightBlack,
    },
    h2: {
      fontSize: 16,
      fontWeight: 400,
      lineHeight: "20px",
      color: colorsBeeline.black,
    },
    h3: {
      fontSize: 14,
      fontWeight: 400,
      lineHeight: "20px",
      color: colorsBeeline.lightBlack,
    },
  },

  components: {
    MuiButton: {
      defaultProps: {
        disableRipple: false,
      },
      styleOverrides: {
        root: ({ ownerState }) => ({
          boxShadow: "none",
          textTransform: "none",
          borderRadius: "8px",

          "&:hover": {
            boxShadow: "none",
          },

          ...(ownerState.variant === "contained" &&
            ownerState.color === "primary" && {
              fontSize: 16,
              fontWeight: 500,
              lineHeight: 1,
              padding: "12px 36px",
              height: "fit-content",
              minHeight: "fit-content",
              border: `1px solid ${colorsBeeline.black}`,
            }),

          ...(ownerState.variant === "outlined" &&
            ownerState.color === "primary" && {
              fontSize: 16,
              fontWeight: 500,
              lineHeight: 1,
              padding: "12px 36px",
              height: "fit-content",
              minHeight: "fit-content",
            }),
        }),
      },
    },
  },
})
